/**
 * Build a user-facing message from a plupload "Error" event object.
 *
 * plupload puts the raw `xhr.responseText` into `error.response`. That text is
 * an empty string whenever the request fails at the network level (the status
 * is then 0: connection reset, offline, proxy closed the connection) or when a
 * gateway answers an error status with an empty body. It can also hold an HTML
 * error page instead of JSON. So the response must never be passed straight to
 * JSON.parse: `JSON.parse("")` throws "Unexpected end of JSON input", which
 * crashes the error handler itself and hides the real problem from the user.
 *
 * plupload also raises the same "Error" event for problems that have no HTTP
 * response at all (file too large, wrong file type, init failure). Those carry
 * `code` and `message` but no `status`, so they keep their own message.
 */

const NETWORK_ERROR_MESSAGE =
    "The connection was lost before the upload finished. Please check your internet connection and try again.";

const GENERIC_ERROR_MESSAGE = "The upload failed. Please try again.";

/**
 * Read the HTTP status of a plupload error.
 * Returns null when the error did not come from an HTTP response.
 */
const readHttpStatus = (error) => {
    if (error.status === undefined || error.status === null || error.status === "") {
        return null;
    }

    const status = Number(error.status);

    return Number.isFinite(status) ? status : null;
};

/**
 * Read the "message" field out of a JSON response body.
 * Returns an empty string when the body is empty or is not JSON.
 */
const readResponseMessage = (response) => {
    if (typeof response !== "string" || response.trim() === "") {
        return "";
    }

    let parsed = null;

    try {
        parsed = JSON.parse(response);
    } catch (e) {
        // Empty body, HTML error page or a truncated response.
        return "";
    }

    return parsed && typeof parsed.message === "string" ? parsed.message : "";
};

/**
 * Turn a plupload error into a { message, code } pair.
 * `code` is null when there is no useful code to show.
 */
const parseUploadError = (error) => {
    const source = error || {};
    const status = readHttpStatus(source);
    const message = readResponseMessage(source.response);

    if (message !== "") {
        return { message, code: status ?? source.code ?? null };
    }

    if (status === 0) {
        // The request never reached the server, or no response came back.
        return { message: NETWORK_ERROR_MESSAGE, code: null };
    }

    const fallback =
        typeof source.message === "string" && source.message.trim() !== ""
            ? source.message
            : GENERIC_ERROR_MESSAGE;

    return { message: fallback, code: status ?? source.code ?? null };
};

/**
 * Format a plupload error as a single line for a notification.
 */
const formatUploadError = (error) => {
    const { message, code } = parseUploadError(error);

    if (code === null || code === "" || code === 0) {
        return message;
    }

    return `Error ${code}: ${message}`;
};

export {
    parseUploadError,
    formatUploadError,
    NETWORK_ERROR_MESSAGE,
    GENERIC_ERROR_MESSAGE,
};
